import { useRef, useState } from 'react';
import { useCopyToClipboard, useUnmount, useUpdateEffect } from 'react-use';

import { UseCopyFn, UseCopyReturn, UseCopyStatus } from './useCopy.types';

/** Represents the hook for copying text to clipboard. */
export function useCopy(): UseCopyReturn {
  const timeoutHandle = useRef<ReturnType<typeof setTimeout>>();
  const [state, copyToClipboard] = useCopyToClipboard();
  const [status, setStatus] = useState<UseCopyStatus>('ready');

  const copy: UseCopyFn = (value) => {
    clearTimeout(timeoutHandle.current);

    timeoutHandle.current = setTimeout(() => {
      resetStatus();
    }, 3000);

    copyToClipboard(value);
  };

  const resetStatus = () => {
    clearTimeout(timeoutHandle.current);
    setStatus('ready');
  };

  // Handles setting the status during the copy process.
  useUpdateEffect(() => {
    // Fast Refresh re-runs effects while keeping refs, which `useUpdateEffect`
    // sees as an update, so only react once something was actually copied.
    if (state.value === undefined && !state.error) {
      return;
    }

    if (state.error) {
      clearTimeout(timeoutHandle.current);
      setStatus('error');
    } else {
      setStatus('copied');
    }
  }, [state]);

  // Cleans up the timeout handler.
  useUnmount(() => clearTimeout(timeoutHandle.current));

  return [
    copy,
    {
      status,
      resetStatus,
    },
  ];
}
